import { useHttp } from 'hooks/use-fetch';
import { fetchWaypointTracking } from 'pages/Dashboard/services/tracking.services';
import { WaypointTracking, WaypointTrackingAPIResponse } from 'pages/Dashboard/types/waypoints.types';
import useSourceData from 'pages/Dashboard/useSourceData';
import { getChartDateRanges, TimeFrameOptions } from 'pages/Dashboard/utils/trackingUtils';
import React from 'react';
import { useQuery } from 'react-query';
import { trackingUrls } from 'utils/apiUrls';

interface SleepDataProps {
    waypointId: string;
    providerId:string;
    patientId:string;
    timeFrame:string;
}
const useActivityData = ({
  waypointId,
  providerId,
  patientId,
  timeFrame,
} : SleepDataProps) => {
  const {
    sourceId,
    sourceOptions,
    onChangeSource,
    sourceObject,
    isSourceFetched,
    isSourceLoading,
  } = useSourceData({
    providerId,
    waypointId,
    patientId,
  });
  const { http } = useHttp();
  const fetchActivityData = useQuery(
    [
      `${trackingUrls.waypoint.queryUrl}-${sourceId}-${timeFrame}`,
      providerId,
      patientId,
      waypointId,
      timeFrame,
      sourceId,
    ],
    {
      queryFn: fetchWaypointTracking(http.get),
      enabled:
            !!providerId && !!patientId && !!isSourceFetched && !!waypointId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const appleHealthActivity = React.useMemo(
    () => fetchActivityData
      ?.data as WaypointTrackingAPIResponse,
    [fetchActivityData],
  );

  const hasData = React.useMemo(
    () => sourceOptions?.length > 0,
    [sourceOptions?.length],
  );

  const appleHealthActivityData = React.useMemo(
    () => appleHealthActivity?.waypoints || [],
    [appleHealthActivity?.waypoints],
  );

  const appleHealthActivityGoals = React.useMemo(
    () => appleHealthActivity?.goals || [],
    [appleHealthActivity?.goals],
  );

  const hasStandHours = React.useMemo(
    () => appleHealthActivityData?.some((w: WaypointTracking) => w?.metadata?.some((m) => m?.key === 'appleStandHours' && parseFloat(m?.value) > -1)),
    [appleHealthActivityData],
  );
  const barSize = 10;
  const appleHealthActivityAvgData = React.useMemo(() => {
    const data = appleHealthActivity || {};
    return {
      value: data?.absoluteAvgValue,
      unit: data?.absoluteValueUnit,
    };
  }, [appleHealthActivity]);
  const yAxisProps = getChartDateRanges(timeFrame as TimeFrameOptions) as {
    startTime: number;
    endTime: number;
    ticks: number[];
    isInDays: boolean;
  };
  return {
    appleHealthActivityData,
    appleHealthActivityGoals,
    appleHealthActivityAvgData,
    hasStandHours,
    barSize,
    sourceOptions,
    sourceId,
    onChangeSource,
    yAxisProps,
    isLoading:
      isSourceLoading || (sourceOptions?.length === 1 && fetchActivityData.isLoading),
    sourceObject,
    hasData,
  };
};

export default useActivityData;
